'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';

interface TickerItem {
  id: string;
  symbol: string;
  price: number;
  change: number;
  changePercent: number;
}

export default function Header() {
  const pathname = usePathname();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  // Mock ticker data (in production, fetch from API)
  const tickerData: TickerItem[] = [
    { id: 'ticker_nifty', symbol: 'NIFTY 50', price: 21823.45, change: 125.30, changePercent: 0.58 },
    { id: 'ticker_sensex', symbol: 'SENSEX', price: 72240.26, change: -89.45, changePercent: -0.12 },
    { id: 'ticker_dow', symbol: 'DOW JONES', price: 38671.69, change: 234.12, changePercent: 0.61 },
    { id: 'ticker_gold', symbol: 'GOLD', price: 62450.00, change: 180.00, changePercent: 0.29 },
    { id: 'ticker_silver', symbol: 'SILVER', price: 74230.00, change: -420.00, changePercent: -0.56 },
    { id: 'ticker_usdinr', symbol: 'USD/INR', price: 83.12, change: 0.08, changePercent: 0.10 },
  ];

  const navLinks = [
    { id: 'nav_home', label: 'Home', href: '/homepage' },
    { id: 'nav_markets', label: 'Markets', href: '/markets' },
    { id: 'nav_news', label: 'News', href: '/news' },
    { id: 'nav_guides', label: 'Guides', href: '/guides' },
    { id: 'nav_faq', label: 'FAQ', href: '/faq' },
  ];

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-background border-b border-border">
      {/* Live Ticker */}
      <div className="bg-card border-b border-border overflow-hidden">
        <div className="ticker-scroll flex items-center gap-8 py-2 px-4">
          {/* Duplicate ticker items for seamless loop */}
          {[...tickerData, ...tickerData].map((item, index) => (
            <div
              key={`${item.id}_${index}`}
              className="flex items-center gap-3 whitespace-nowrap"
            >
              <span className="text-sm font-semibold text-foreground font-mono">
                {item.symbol}
              </span>
              <span className="text-sm font-mono text-foreground">
                {item.price.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
              </span>
              <span
                className={`flex items-center gap-1 text-xs font-mono ${
                  item.change >= 0 ? 'text-primary' : 'text-secondary'
                }`}
              >
                <Icon
                  name={item.change >= 0 ? 'ArrowUpIcon' : 'ArrowDownIcon'}
                  size={14}
                  variant="solid"
                />
                {Math.abs(item.changePercent).toFixed(2)}%
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Main Navigation */}
      <nav className="container mx-auto px-4 py-4 flex items-center justify-between max-w-7xl">
        {/* Logo */}
        <Link href="/homepage" className="flex items-center gap-2 group">
          <div className="w-10 h-10 bg-gradient-to-br from-primary to-accent rounded-lg flex items-center justify-center">
            <span className="text-xl font-bold text-gray-900">M</span>
          </div>
          <div className="flex flex-col">
            <span className="text-xl font-bold text-foreground font-display tracking-tight">
              MoneyMine
            </span>
            <span className="text-[10px] text-muted-foreground uppercase tracking-wider">
              Financial Intelligence
            </span>
          </div>
        </Link>

        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => (
            <Link
              key={link.id}
              href={link.href}
              className={`text-sm font-medium transition-colors hover:text-primary ${
                pathname === link.href
                  ? 'text-primary' :'text-muted-foreground'
              }`}
            >
              {link.label}
            </Link>
          ))}
        </div>

        {/* Search & Menu Icons */}
        <div className="flex items-center gap-4">
          <button
            className="p-2 hover:bg-muted rounded-lg transition-colors"
            aria-label="Search"
          >
            <Icon name="MagnifyingGlassIcon" size={20} className="text-foreground" />
          </button>

          {/* Mobile Menu Toggle */}
          <button
            className="md:hidden p-2 hover:bg-muted rounded-lg transition-colors"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Toggle menu"
          >
            <Icon
              name={isMenuOpen ? 'XMarkIcon' : 'Bars3Icon'}
              size={24}
              className="text-foreground"
            />
          </button>
        </div>
      </nav>

      {/* Mobile Menu */}
      {isHydrated && isMenuOpen && (
        <div className="md:hidden bg-card border-t border-border">
          <div className="container mx-auto px-4 py-6 flex flex-col gap-4">
            {navLinks.map((link) => (
              <Link
                key={link.id}
                href={link.href}
                className={`text-base font-medium py-2 transition-colors ${
                  pathname === link.href
                    ? 'text-primary' :'text-muted-foreground hover:text-primary'
                }`}
                onClick={() => setIsMenuOpen(false)}
              >
                {link.label}
              </Link>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}